import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { CTAButton } from "./bits";
import { HeroCanvas } from "./HeroCanvas";
import { scrollToId, BRAND } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];

const MaskLine = ({ children, delay, reduce }) => (
  <span className="-mb-[0.1em] block overflow-hidden pb-[0.1em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: reduce ? 0 : "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const canvasY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 140]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 70]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="home"
      data-testid="hero-section"
      ref={ref}
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.div style={{ y: canvasY }} className="absolute inset-0 lg:left-[36%]">
        <HeroCanvas className="h-full w-full opacity-60 lg:opacity-100" />
      </motion.div>
      <div className="animate-glow pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_28%,rgba(16,185,129,0.09),transparent_55%)]" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#F8F7F3] via-[#F8F7F3]/70 to-transparent lg:via-[#F8F7F3]/35" />

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-28 pt-36"
      >
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.9, ease: EASE }}
        >
          <div
            data-testid="amfi-badge"
            className="animate-float inline-flex items-center gap-2.5 rounded-full border border-emerald-700/20 bg-[#DDF7EC] px-4 py-2"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-emerald-800/90">
              {BRAND.badge}
            </span>
          </div>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="mt-9 font-sans text-[13vw] font-semibold leading-[1.04] tracking-tight sm:text-6xl lg:text-7xl xl:text-[5.4rem]"
        >
          <MaskLine delay={0.35} reduce={reduce}>
            <span className="text-gradient-champagne">Your path to</span>
          </MaskLine>
          <MaskLine delay={0.48} reduce={reduce}>
            <span className="font-display font-medium italic text-emerald-600">financial</span>
          </MaskLine>
          <MaskLine delay={0.61} reduce={reduce}>
            <span className="text-gradient-champagne">freedom.</span>
          </MaskLine>
        </h1>

        <motion.p
          data-testid="hero-subcopy"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 1, ease: EASE }}
          className="mt-8 max-w-xl text-lg font-light leading-relaxed text-[#33506A] sm:text-xl"
        >
          Thoughtful financial guidance designed around your goals, your life and your future.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 1, ease: EASE }}
          className="mt-11 flex flex-wrap items-center gap-4"
        >
          <CTAButton testId="hero-primary-cta" onClick={() => scrollToId("contact")}>
            Start a conversation
          </CTAButton>
          <CTAButton testId="hero-secondary-cta" variant="ghost" onClick={() => scrollToId("services")}>
            Explore our services
          </CTAButton>
        </motion.div>
      </motion.div>

      <motion.button
        type="button"
        data-testid="scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        onClick={() => scrollToId("problem")}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3"
        aria-label="Scroll to next section"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#6E8698]">Scroll</span>
        <span className="relative h-10 w-px overflow-hidden bg-[#10202E]/10">
          <span className="animate-scroll-dot absolute left-0 top-0 h-3 w-px bg-emerald-500" />
        </span>
      </motion.button>
    </section>
  );
};
